import { useCallback, useState } from 'react';
import { useSearchParam } from 'react-use';

const CONVERSATION_PARAM = 'conversation';

export const useConversationId = (searchPath?: string, widgetKey?: string) => {
  const conversationId = useSearchParam(CONVERSATION_PARAM);

  // Unfortunately, the search param is not updated when the url is changed in Next.js
  // As a workaround, we use a local state to store the conversation id
  // and update the url when the local state changes
  // https://github.com/vercel/next.js/discussions/18072
  // https://github.com/vercel/next.js/discussions/70779
  const [_conversationId, _setConversationId] = useState(conversationId ?? undefined);

  const setConversationId = useCallback(
    (id: string) => {
      const url = new URL(window.location.href);
      if (searchPath) {
        url.pathname = searchPath;
      }
      if (id) {
        url.searchParams.set(CONVERSATION_PARAM, id);
      } else {
        url.searchParams.delete(CONVERSATION_PARAM);
      }
      const newUrl = url.toString();
      window.history.replaceState({ ...window.history.state, newUrl, url: newUrl }, '', newUrl);
      _setConversationId(id);

      // Notify the parent frame so it can persist the conversation ID in localStorage.
      // widgetKey is included so each listener only processes its own widget's events.
      if (typeof window !== 'undefined' && window.parent !== window) {
        window.parent.postMessage({ type: 'CONVERSATION_CHANGED', conversationId: id, widgetKey }, '*');
      }
    },
    [searchPath, widgetKey],
  );

  const clearConversationId = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(CONVERSATION_PARAM);
    const newUrl = url.toString();
    window.history.replaceState({ ...window.history.state, newUrl, url: newUrl }, '', newUrl);
    _setConversationId(undefined);

    // Notify the parent frame to clear the persisted conversation ID from localStorage.
    // widgetKey is included so each listener only processes its own widget's events.
    if (typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage({ type: 'CONVERSATION_CHANGED', conversationId: '', widgetKey }, '*');
    }
  }, [widgetKey]);

  return { conversationId: _conversationId, setConversationId, clearConversationId };
};
